import { Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';
import { LocationService } from 'src/app/services/location.service';
import { LookupService } from 'src/app/services/lookup.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { TrimTextTransformPipe } from 'src/app/shared/pipes/trim-text-transform.pipe';
import { AuthService } from '../../auth/auth.service';
import { EventStepperDataService } from './event-stepper-data.service';

@Component({
  selector: 'ness-event-locations',
  templateUrl: './event-locations.component.html',
  providers: [TrimTextTransformPipe]
})
export class EventLocationsComponent implements OnInit, OnDestroy {
  addEditLocationTitleText: string = null;
  displaySidebar: boolean = false;
  associationButtonLabel: string = "Associate with event";
  eventForm: FormGroup;
  eventLocationsFormControl: FormControl;
  findLocationFormGroup: FormGroup;
  hasEditPermission = this.authService.hasUserTypeRole(AppPermissions.canEditLocation.roleUserType);
  locationFormMode: string = null;
  locationSearchResultTableCols = [
    { field: 'StreetAddress', header: 'Address' },
    { field: 'CityName', header: 'City' },
    { field: 'StateName', header: 'State' },
    { field: 'PostalCode', header: 'Postal code' }
  ];
  openNewLocationForm: boolean = false;
  openLocationViewDetail: boolean = false;
  rows: number = AppConstants.defaultTableRows;
  searchAddress$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  selectedAddresses: ServerModels.ENESS_AddressDTO[] = [];
  showLocationDetailsView: boolean = true;
  showEditRMSLocationView: boolean = false;

  protected destroyed$: Subject<void> = new Subject();

  public get locations(): ServerModels.ENESS_XREF_EventsLocationsDTO[] {
    return this.eventLocationsFormControl.value || [];
  }

  constructor(public controlContainer: FormGroupDirective,
    public eventDataSvc: EventStepperDataService,
    public lookupService: LookupService,
    public messageService: MessageService,
    private authService: AuthService,
    private locationService: LocationService,
    private fb: FormBuilder,
    private trimTextTransform: TrimTextTransformPipe,
    private domSanitizer: DomSanitizer) {  }

  ngOnInit() {
    this.eventForm = <FormGroup>this.controlContainer.form;
    this.eventLocationsFormControl = <FormControl>this.eventForm.get('ENESS_XREF_EventsLocations');
    this.findLocationFormGroup = this.fb.group({
      FindLocationStreetAddressID: [null, [Validators.required, Validators.maxLength(80)]],
      FindLocationUnitID: [null, [Validators.maxLength(80)]],
      FindLocationCityNameID: null,
      FindLocationStateID: '',
      FindLocationPostalCodeID: [null, [Validators.pattern('[0-9]{5}')]],
      FindLocationPostalCodeExtendedID: [null, [Validators.pattern('[0-9]{4}')]]
    });
    this.onSearchLocationChanges();
  }

  ngOnDestroy() {
    this.eventDataSvc.setAddressResults([]);
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onMaskBlur(field: string): void {
    let ctrl = this.findLocationFormGroup.get(field);
    if (ctrl.invalid) {
      ctrl.reset();
    }
  }

  public onSearchLocation() {
    if (this.findLocationFormGroup.valid) {
      let locationSearch = this.findLocationFormGroup.value;
      let streetAddressValue = this.trimTextTransform.transform(locationSearch.FindLocationStreetAddressID);
      let unitValue = this.trimTextTransform.transform(locationSearch.FindLocationUnitID);
      let cityNameValue = this.trimTextTransform.transform(locationSearch.FindLocationCityNameID);
      let stateValue = this.trimTextTransform.transform(locationSearch.FindLocationStateID);
      let postalCodeValue = this.trimTextTransform.transform(locationSearch.FindLocationPostalCodeID);
      let postalCodeExtendedValue = this.trimTextTransform.transform(locationSearch.FindLocationPostalCodeExtendedID);

      this.eventDataSvc.findAddresses(streetAddressValue, unitValue, cityNameValue, stateValue, postalCodeValue, postalCodeExtendedValue);
    }
  }

  public onSearchLocationChanges(): void {
    this.findLocationFormGroup.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((val:any) => {
      if (!!val) {
        let streetAddressValue = !!val['FindLocationStreetAddressID'] ? val['FindLocationStreetAddressID'].trim() : "";
        let unitValue = !!val['FindLocationUnitID'] ? ", " + val['FindLocationUnitID'].trim() : "";
        let cityNameValue = !!val['FindLocationCityNameID'] ? ", " + val['FindLocationCityNameID'].trim() : "";
        let stateValue = !!val['FindLocationStateID'] ? ", " + val['FindLocationStateID'].trim() : "";
        let postalCodeValue = !!val['FindLocationPostalCodeID'] ? ", " + val['FindLocationPostalCodeID'].trim() : "";
        let postalCodeExtendedValue = !!val['FindLocationPostalCodeExtendedID'] ? "-" + val['FindLocationPostalCodeExtendedID'].trim() : "";

        this.searchAddress$.next(this.domSanitizer.sanitize(SecurityContext.HTML, streetAddressValue + unitValue + cityNameValue + stateValue + postalCodeValue + postalCodeExtendedValue));
      }
    });
  }

  public associateSelectedLocations(): void {
    this.messageService.clear();
    let locations = this.selectedAddresses.map((address: ServerModels.ENESS_AddressDTO) => {
      if (this._isDuplicate(address.AddressID)) {
        this.messageService.add({ key: 'eventLocationMsg', severity: 'info', summary: 'Info:', detail: `"${address.StreetAddress}" is already associated with this event.` });
        return null;
      } else {
        return <ServerModels.ENESS_XREF_EventsLocationsDTO>{
          EventLocationStagingID: null,
          EventLocationID: null,
          EventStagingID: this.eventForm.get('EventStagingID').value,
          EventID: this.eventDataSvc.eventId,
          AddressStagingID: null,
          AddressID: address.AddressID,
          CreatedOn: null,
          CreatedBy: null,
          ModifiedBy: null,
          ModifiedOn: null,
          ENESS_Address: null,
          ENESS_Event: null
        };
      }
    }, this);

    locations = locations.filter(x => !!x);

    if (locations.length > 0) {
      this.eventDataSvc.addExistingEventLocationsToStaging(locations).pipe(takeUntil(this.destroyed$)).subscribe((newLocations: ServerModels.ENESS_XREF_EventsLocationsDTO[]) => {
        this.eventLocationsFormControl.setValue([...newLocations, ...this.locations]);
        this.eventLocationsFormControl.updateValueAndValidity();
        this.resetLocationSearch();
      },
        (err:any) => {
          if (err.status !== 0) {
            this.messageService.add({ key: 'eventLocationMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
          }
        }
      );
    }
  }

  public onAddNewLocationClick(): void {
    this.displaySidebar = true;
    this.openNewLocationForm = true;
    this.locationFormMode = 'add';
    this.addEditLocationTitleText = 'Add a new location';
  }

  public removeLocation(idx: number): void {
    let eventLocationStagingId: string = this.locations[idx].EventLocationStagingID;
    this.eventDataSvc.deleteEventLocation(eventLocationStagingId).pipe(take(1)).subscribe((result:number) => {
      if (result > -1) {
        this.locations.splice(idx, 1);
        this.eventLocationsFormControl.setValue([...this.locations]);
      }
    });
  }

  public viewLocationDetails(stagingId: string, addressId: string, source: string): void {
    this.displaySidebar = true;
    this.openLocationViewDetail = true;

    if (source == 'associated') {
      this.eventDataSvc.getLocationDetailsStaging(stagingId);
    }
    if (source == 'find') {
      this.eventDataSvc.getLocationDetails(addressId);
    }

    if (!!addressId) {
      this.locationService.getAddressPeople(addressId).pipe(takeUntil(this.destroyed$)).subscribe((people: ServerModels.AddressPeople[]) => this.locationService.addressPeopleSubj.next(people));
      this.locationService.getAddressLECases(addressId).pipe(takeUntil(this.destroyed$)).subscribe((cases: ServerModels.AddressLECases[]) => this.locationService.addressLECasesSubj.next(cases));
    } else {
      this.locationService.addressPeopleSubj.next([]);
      this.locationService.addressLECasesSubj.next([]);
    }
  }

  public onNewLocationAdded(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.messageService.clear();

    if (newAddress != null && !!Object.keys(newAddress).length) {
      let newLocation = <ServerModels.ENESS_XREF_EventsLocationsDTO>{
        EventLocationStagingID: null,
        EventLocationID: null,
        EventStagingID: this.eventForm.get('EventStagingID').value,
        EventID: this.eventDataSvc.eventId,
        AddressStagingID: null,
        AddressID: null,
        CreatedOn: null,
        CreatedBy: null,
        ENESS_Address: newAddress
      };

      this.eventDataSvc.addOrUpdateEventLocationStaging(newLocation).pipe(takeUntil(this.destroyed$)).subscribe((newLocations: ServerModels.ENESS_XREF_EventsLocationsDTO) => {
        this.eventLocationsFormControl.setValue([newLocations, ...this.locations]);
        this.eventLocationsFormControl.updateValueAndValidity();
        this.resetLocationSearch();
      },
        (err:any) => {
          if (err.status !== 0) {
            this.messageService.add({ key: 'eventLocationMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
          }
        }
      );
    }
  }

  public onNewLocationClose(): void {
    this.searchAddress$.next(null);
    this.findLocationFormGroup.reset();
    this.eventDataSvc.setAddressResults([]);
    this.openNewLocationForm = false;
    this.displaySidebar = false;
  }

  public onLocationDetailEditClose(): void {
    if (this.showEditRMSLocationView) {
      this.showEditRMSLocationView = false;
      this.showLocationDetailsView = true;
    }
    else if (this.showLocationDetailsView) {
      this.eventDataSvc.setLocationAddress(null);
      this.openLocationViewDetail = false;
      this.displaySidebar = false;
    }
  }

  public onCloseSidebar(): void {
    this.findLocationFormGroup.reset();
    this.eventDataSvc.setLocationAddress(null);
    this.openLocationViewDetail = false;
    this.openNewLocationForm = false;
    this.displaySidebar = false;
    this.onLocationDetailEditClose();
  }

  private _isDuplicate(addressId: string): boolean {
    return this.locations.some(x => addressId === x.AddressID || addressId === x.AddressStagingID);
  }

  private resetLocationSearch(): void {
    this.selectedAddresses = [];
    this.onNewLocationClose();
  }

  public showEditLocation(evt: string): void {
    this.showLocationDetailsView = false;
    this.showEditRMSLocationView = true;
  }

  public onLocationFormSaved(location: ServerModels.ENESS_AddressDTO): void {
    this.showEditRMSLocationView = false;
    this.showLocationDetailsView = true;
    let index = this.locations.findIndex(l => l.AddressStagingID == location.AddressStagingID);
    if (index !== -1) {
      this.locations[index].ENESS_Address = location;
      this.locations[index].AddressStagingID = location.AddressStagingID;
      this.locations[index].AddressID = location.AddressID;
      this.locations[index].CityName = location.CityName;
      this.locations[index].PostalCode = location.PostalCode;
      this.locations[index].StateName = location.StateName;
      this.locations[index].StreetAddress = location.StreetAddress;
      this.locations[index].Unit = location.Unit;
    }
    this.eventLocationsFormControl.setValue([...this.locations]);
    this.eventLocationsFormControl.updateValueAndValidity();
  }
}
